/* eslint-disable react/prop-types */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { API_URL } from "config";

// Material UI Components
import {
  Card,
  CircularProgress,
  Box,
  Divider,
  Chip,
  Avatar,
  Typography,
  Tooltip,
  Paper,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from "@mui/material";
import { styled } from "@mui/material/styles";

// Material UI Icons
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import WorkIcon from "@mui/icons-material/Work";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CakeIcon from "@mui/icons-material/Cake";
import SchoolIcon from "@mui/icons-material/School";
import BadgeIcon from "@mui/icons-material/Badge";
import ScheduleIcon from "@mui/icons-material/Schedule";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import SecurityIcon from "@mui/icons-material/Security";
import BusinessIcon from "@mui/icons-material/Business";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ReceiptIcon from "@mui/icons-material/Receipt";
import FolderIcon from "@mui/icons-material/Folder";
import VerifiedIcon from "@mui/icons-material/Verified";
import StarIcon from "@mui/icons-material/Star";
import DescriptionIcon from "@mui/icons-material/Description";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import School from "@mui/icons-material/School";
import SchoolOutlined from "@mui/icons-material/SchoolOutlined";
import WorkspacePremium from "@mui/icons-material/WorkspacePremium";
import MilitaryTech from "@mui/icons-material/MilitaryTech";
import KeyIcon from "@mui/icons-material/Key";

// Custom Soft UI Components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import SoftSnackbar from "components/SoftSnackbar";
import FormField from "layouts/pages/account/components/FormField";
import FlagLoader from "layouts/flagloader";

const API_ORIGIN = API_URL.replace(/\/api\/?$/, "");
const BASE_STORAGE_URL = `${API_ORIGIN}/storage`;

// Axios interceptor
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("authToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Styled Components
const ProfileCard = styled(Card)(({ theme }) => ({
  borderRadius: "10px",
  border: "none",
  boxShadow: "0 3px 16px rgba(0,0,0,0.06)",
  transition: "all 0.3s ease",
  "&:hover": {
    boxShadow: "0 6px 24px rgba(0,0,0,0.1)",
    transform: "translateY(-1px)",
  },
}));

const StatCard = styled(Paper)(({ theme }) => ({
  padding: "6px",
  borderRadius: "8px",
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  color: "white",
  height: "100%",
}));

const InfoCard = styled(Card)(({ theme }) => ({
  borderRadius: "8px",
  border: "1px solid #f0f0f0",
  background: "#fff",
  marginBottom: "12px",
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "0.95rem",
  fontWeight: 600,
  color: "#333",
  marginBottom: "12px",
  paddingBottom: "6px",
  borderBottom: "1px solid #667eea",
  display: "flex",
  alignItems: "center",
  gap: "6px",
}));

const ValueText = styled(Typography)(({ theme }) => ({
  fontSize: "0.9rem",
  fontWeight: 500,
  color: "#444",
}));

const LabelText = styled(Typography)(({ theme }) => ({
  fontSize: "0.9rem",
  fontWeight: 600,
  color: "#333",
}));

const CompactInfoRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  "& svg": {
    fontSize: "15px",
    color: "#666",
    flexShrink: 0,
  },
}));


const CompactValue = styled(Typography)(({ theme }) => ({
  fontSize: "0.8rem",
  fontWeight: 500,
  color: "#333",
}));

function UserProfile() {
  const id = localStorage.getItem("loggedUserId");
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [error, setError] = useState(null);

  // Change Password Dialog States
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current_password: "",
    new_password: "",
    new_password_confirmation: "",
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    color: "success",
  });

  const getDocumentUrl = (url) => {
    if (!url) return "";

    const value = String(url).trim();
    if (!value || value === BASE_STORAGE_URL || value.endsWith("/storage")) return "";
    if (/^https?:\/\//i.test(value)) return value;
    if (value.startsWith("/storage/")) return `${API_ORIGIN}${value}`;

    return `${BASE_STORAGE_URL}/${value.replace(/^\/+/, "")}`;
  };

  const isValidDocUrl = (url) => Boolean(getDocumentUrl(url));

  const handleViewDocument = (url) => {
    const documentUrl = getDocumentUrl(url);
    if (!documentUrl) return;

    window.open(documentUrl, "_blank", "noopener,noreferrer");
  };

  useEffect(() => {
    fetchUserProfile();
  }, [id]);

  const fetchUserProfile = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/users/my/profile`);
      if (response.data.success) {
        setUserData(response.data.user);
      } else {
        setError("Failed to load user data");
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
      setError("Unable to load user profile. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return "₹0";
    const num = parseFloat(amount);
    if (num >= 10000000) return `₹${(num / 10000000).toFixed(1)}Cr`;
    if (num >= 100000) return `₹${(num / 100000).toFixed(1)}L`;
    return `₹${num.toLocaleString("en-IN")}`;
  };

  // Change Password Functions
  const handleOpenChangePassword = () => {
    setChangePasswordOpen(true);
    setPasswordForm({
      current_password: "",
      new_password: "",
      new_password_confirmation: "",
    });
    setPasswordErrors({});
  };

  const handleCloseChangePassword = () => {
    setChangePasswordOpen(false);
  };

  const handlePasswordChange = (field) => (e) => {
    setPasswordForm({
      ...passwordForm,
      [field]: e.target.value,
    });

    // Clear error for this field
    if (passwordErrors[field]) {
      setPasswordErrors({
        ...passwordErrors,
        [field]: "",
      });
    }
  };

  const validatePasswordForm = () => {
    const newErrors = {};

    if (!passwordForm.current_password) {
      newErrors.current_password = "Current password is required";
    }

    if (!passwordForm.new_password) {
      newErrors.new_password = "New password is required";
    } else if (passwordForm.new_password.length < 6) {
      newErrors.new_password = "Password must be at least 6 characters";
    }

    if (passwordForm.new_password !== passwordForm.new_password_confirmation) {
      newErrors.new_password_confirmation = "Passwords do not match";
    }

    setPasswordErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChangePasswordSubmit = async () => {
    if (!validatePasswordForm()) return;

    setChangePasswordLoading(true);
    try {
      const response = await axios.put(`${API_URL}/users/change-password/${id}`, passwordForm, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });

      setSnackbar({
        open: true,
        message: response.data.message || "Password updated successfully!",
        color: "success",
      });

      // Reset form and close dialog
      setPasswordForm({
        current_password: "",
        new_password: "",
        new_password_confirmation: "",
      });
      setChangePasswordOpen(false);
    } catch (error) {
      console.error("Password change error:", error);

      if (error.response?.data?.errors) {
        setPasswordErrors(error.response.data.errors);
        setSnackbar({
          open: true,
          message: "Please fix the errors in the form",
          color: "error",
        });
      } else {
        setSnackbar({
          open: true,
          message: error.response?.data?.message || "Failed to update password",
          color: "error",
        });
      }
    } finally {
      setChangePasswordLoading(false);
    }
  };

  const educationRows = [
    {
      level: "10th",
      exam: userData?.tenth_exam_passed,
      board: userData?.tenth_board,
      institute: userData?.tenth_institution_name,
      year: userData?.tenth_year_of_passing,
      percentage: userData?.tenth_percentage,
      subject: userData?.tenth_subject,
      mode: userData?.tenth_mode,
    },
    {
      level: "12th",
      exam: userData?.twelth_exam_passed,
      board: userData?.twelth_board,
      institute: userData?.twelth_institution_name,
      year: userData?.twelth_year_of_passing,
      percentage: userData?.twelth_percentage,
      subject: userData?.twelth_subject,
      mode: userData?.twelth_mode,
    },
    {
      level: "Graduation",
      exam: userData?.graduation_exam_passed,
      board: userData?.graduation_board,
      institute: userData?.graduation_institution_name,
      year: userData?.graduation_year_of_passing,
      percentage: userData?.graduation_percentage,
      subject: userData?.graduation_subject,
      mode: userData?.graduation_mode,
    },
    {
      level: "Post Graduation",
      exam: userData?.post_graduation_exam_passed,
      board: userData?.post_graduation_board,
      institute: userData?.post_graduation_institution_name,
      year: userData?.post_graduation_year_of_passing,
      percentage: userData?.post_graduation_percentage,
      subject: userData?.post_graduation_subject,
      mode: userData?.post_graduation_mode,
    },
  ];

  const calculateSalaryBreakup = () => {
    if (!userData) return {};

    const earningsMonthly =
      parseFloat(userData.basic_salary_monthly || 0) +
      parseFloat(userData.hra_monthly || 0) +
      parseFloat(userData.dearness_allowance_monthly || 0) +
      parseFloat(userData.conveyance_allowance_monthly || 0);

    const deductionsMonthly =
      parseFloat(userData.pf_monthly || 0) +
      parseFloat(userData.esi_monthly || 0) +
      parseFloat(userData.lta_monthly || 0);

    const inHandMonthly = earningsMonthly - deductionsMonthly;

    return {
      earningsMonthly,
      earningsAnnual: earningsMonthly * 12,
      deductionsMonthly,
      deductionsAnnual: deductionsMonthly * 12,
      inHandMonthly,
      inHandAnnual: inHandMonthly * 12,
    };
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box
          className="d-flex justify-content-center align-items-center"
          style={{ height: "50vh" }}
        >
          {/* <CircularProgress size={40} /> */}
          {/* <Typography variant="body1" className="ms-2" fontSize="0.9rem">
            Loading profile...
          </Typography> */}
          <FlagLoader />
        </Box>
      </DashboardLayout>
    );
  }

  if (error || !userData) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box className="p-3">
          <ProfileCard>
            <Box className="p-3 text-center">
              <Typography variant="h6" color="error" className="mb-2" fontSize="0.95rem">
                {error || "No user data found"}
              </Typography>
              <SoftButton variant="gradient" color="info" size="small" onClick={fetchUserProfile}>
                Try Again
              </SoftButton>
            </Box>
          </ProfileCard>
        </Box>
      </DashboardLayout>
    );
  }

  const salary = calculateSalaryBreakup();

  const roleName = userData?.primary_role?.name || "N/A";
  const departmentName = userData?.department?.name || "N/A";
  const designationName = userData?.designation?.name || "N/A";
  const branchName = userData?.branch?.name || "N/A";
  const subBranchName = userData?.subBranch?.name || "N/A";
  const teamLeaderName = userData?.team_leader?.name || "N/A";

  const shiftCode = userData?.primary_shift?.shift_code || "N/A";
  const shiftStart = userData?.primary_shift?.start_time;
  const shiftEnd = userData?.primary_shift?.end_time;

  const shiftDisplay =
    shiftStart && shiftEnd ? `${shiftCode} (${shiftStart} - ${shiftEnd})` : shiftCode;

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Box className="container-fluid pb-2">
        {/* Profile Header with Change Password Button */}
        <ProfileCard className="mb-2">
          <Box sx={{ pl: 2, py: 1.3 }}>
            <Grid container spacing={1.5} alignItems="center">
              {/* ================= Avatar ================= */}
              <Grid item xs={12} md="auto" pr={3}>
                <Avatar
                  src={userData.photo}
                  sx={{
                    width: 85,
                    height: 85,
                    border: "2px solid #fff",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                    bgcolor: "#e0e0e0",
                  }}
                >
                  {!userData.photo && <PersonIcon sx={{ fontSize: 32 }} />}
                </Avatar>
              </Grid>

              {/* ================= Main Content ================= */}
              <Grid item xs={12} md sx={{ minWidth: 0 }}>
                {/* ---------- Name + Status Row ---------- */}
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 1,
                    mb: 0.6,
                  }}
                >
                  <Typography variant="h5" fontWeight={600} color="#2c2c2c" fontSize="1.25rem">
                    {userData?.name} {userData?.last_name}
                  </Typography>

                  <Chip
                    label={userData.user_code || "N/A"}
                    size="small"
                    sx={{ fontSize: "0.65rem", height: 20, fontWeight: 500 }}
                  />

                  {userData?.status === "selected" && (
                    <Tooltip title="Verified">
                      <VerifiedIcon sx={{ color: "#4CAF50", fontSize: 18 }} />
                    </Tooltip>
                  )}

                  <Chip
                    label={userData?.status?.toUpperCase() || "PENDING"}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: "0.65rem",
                      bgcolor: userData?.status === "selected" ? "#4CAF50" : "#FF9800",
                      color: "#fff",
                      fontWeight: 500,
                    }}
                  />

                  {/* Change Password Button */}
                  <SoftButton
                    variant="outlined"
                    color="dark"
                    size="small"
                    startIcon={<KeyIcon />}
                    onClick={handleOpenChangePassword}
                    sx={{
                      fontSize: "0.7rem",
                      padding: "3px 10px",
                      ml: "auto",
                      minHeight: "28px",
                    }}
                  >
                    Change Password
                  </SoftButton>
                </Box>

                {/* ---------- Contact Info ---------- */}
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <CompactInfoRow>
                    <EmailIcon fontSize="small" />
                    <CompactValue>{userData?.email}</CompactValue>
                  </CompactInfoRow>

                  {userData?.location && (
                    <CompactInfoRow>
                      <LocationOnIcon fontSize="small" />
                      <CompactValue>{userData?.location}</CompactValue>
                    </CompactInfoRow>
                  )}
                </Box>

                {/* ================= Professional Info ================= */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr 1fr",
                      sm: "repeat(4, 1fr)",
                      md: "repeat(6, 1fr)",
                    },
                    gap: 1,
                    p: 1.2,
                    bgcolor: "white",
                    border: "1px solid #eee",
                    borderRadius: 1.5,
                  }}
                >
                  {[
                    {
                      label: "Branch",
                      // value: branchName,
                      value: subBranchName,
                      icon: <BusinessIcon />,
                    },
                    {
                      label: "Shift",
                      value: shiftDisplay,
                      icon: <ScheduleIcon />,
                    },
                    { label: "Department", value: departmentName || "N/A", icon: <BusinessIcon /> },
                    { label: "Designation", value: designationName || "N/A", icon: <BadgeIcon /> },
                    { label: "Team Leader", value: teamLeaderName, icon: <PersonIcon /> },
                  ]
                    .filter(Boolean)
                    .map((item, index) => (
                      <Box key={index}>
                        <LabelText>{item.label}</LabelText>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                          {React.cloneElement(item.icon, {
                            sx: { fontSize: 13, color: "#666" },
                          })}
                          <CompactValue>{item.value}</CompactValue>
                        </Box>
                      </Box>
                    ))}

                  {/* ================= Dates ================= */}
                  <Grid item xs={12} sx={{ gridColumn: "1 / -1", mt: 0.8 }}>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "repeat(4, 1fr)",
                          md: "repeat(6, 1fr)",
                        },
                        gap: 1.5,
                        pt: 1,
                        borderTop: "1px dashed #ccc",
                      }}
                    >
                      {[
                        { label: "Role", value: roleName },
                        { label: "Interview Date", value: userData.interview_date },
                        { label: "Pre-Joining Date", value: userData.pre_joining_date },
                        { label: "Joining Date", value: userData.joining_date },
                        { label: "Appointment Date", value: userData.appointment_date },
                      ].map((item, index) => (
                        <Box key={index}>
                          <LabelText>{item.label}</LabelText>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            {item.label == "Role" ? (
                              <WorkIcon sx={{ fontSize: 13, color: "#666" }} />
                            ) : (
                              <CalendarTodayIcon sx={{ fontSize: 13, color: "#666" }} />
                            )}
                            {item.label == "Role" ? (
                              <CompactValue>{item.value}</CompactValue>
                            ) : (
                              <CompactValue>{formatDate(item.value)}</CompactValue>
                            )}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </ProfileCard>

        {/* Quick Stats */}
        {branchName?.toLowerCase() !== "noida" && (
          <Box className="row mb-3 g-1">
            <div className="col-sm-6 col-lg-3">
              <StatCard
                elevation={0}
                sx={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}
              >
                <Box className="d-flex align-items-center">
                  <Box className="bg-white rounded-circle p-1 me-2">
                    <AttachMoneyIcon sx={{ color: "#667eea", fontSize: 18 }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ color: "rgba(255,255,255,0.8)", fontSize: "0.65rem" }}
                    >
                      MONTHLY SALARY
                    </Typography>
                    <Typography variant="h6" color="white" fontWeight="600" fontSize="0.95rem">
                      {formatCurrency(salary.inHandMonthly)}
                    </Typography>
                  </Box>
                </Box>
              </StatCard>
            </div>

            <div className="col-sm-6 col-lg-3">
              <StatCard
                elevation={0}
                sx={{ background: "linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)" }}
              >
                <Box className="d-flex align-items-center">
                  <Box className="bg-white rounded-circle p-1 me-2">
                    <ReceiptIcon sx={{ color: "#4CAF50", fontSize: 18 }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ color: "rgba(255,255,255,0.8)", fontSize: "0.65rem" }}
                    >
                      ANNUAL SALARY
                    </Typography>
                    <Typography variant="h6" color="white" fontWeight="600" fontSize="0.95rem">
                      {formatCurrency(salary.inHandAnnual)}
                    </Typography>
                  </Box>
                </Box>
              </StatCard>
            </div>

            <div className="col-sm-6 col-lg-3">
              <StatCard
                elevation={0}
                sx={{ background: "linear-gradient(135deg, #FF9800 0%, #EF6C00 100%)" }}
              >
                <Box className="d-flex align-items-center">
                  <Box className="bg-white rounded-circle p-1 me-2">
                    <SecurityIcon sx={{ color: "#FF9800", fontSize: 18 }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ color: "rgba(255,255,255,0.8)", fontSize: "0.65rem" }}
                    >
                      PF STATUS
                    </Typography>
                    <Typography variant="h6" color="white" fontWeight="600" fontSize="0.95rem">
                      {userData.pf === "yes" ? "Enrolled" : "Not Enrolled"}
                    </Typography>
                  </Box>
                </Box>
              </StatCard>
            </div>

            <div className="col-sm-6 col-lg-3">
              <StatCard
                elevation={0}
                sx={{ background: "linear-gradient(135deg, #9C27B0 0%, #6A1B9A 100%)" }}
              >
                <Box className="d-flex align-items-center">
                  <Box className="bg-white rounded-circle p-1 me-2">
                    <SecurityIcon sx={{ color: "#9C27B0", fontSize: 18 }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ color: "rgba(255,255,255,0.8)", fontSize: "0.65rem" }}
                    >
                      ESI STATUS
                    </Typography>
                    <Typography variant="h6" color="white" fontWeight="600" fontSize="0.95rem">
                      {userData.esi === "yes" ? "Enrolled" : "Not Enrolled"}
                    </Typography>
                  </Box>
                </Box>
              </StatCard>
            </div>
          </Box>
        )}

        {/* Main Content - Vertical Layout */}
        <Box className="row g-1">
          {/* Left Column */}
          <div className="col-lg-4">
            {/* Personal Information */}
            <InfoCard>
              <Box className="p-2">
                <SectionTitle>
                  <PersonIcon fontSize="small" />
                  Personal Information
                </SectionTitle>

                <Box sx={{ "& > div": { mb: 1.5 } }}>
                  <CompactInfoRow>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <CakeIcon sx={{ fontSize: 16, color: "#666" }} />
                      <LabelText>Date of Birth</LabelText>
                    </Box>
                    <ValueText>{formatDate(userData.dob)}</ValueText>
                  </CompactInfoRow>

                  <CompactInfoRow>
                    <LabelText>Gender</LabelText>
                    <ValueText>{userData.gender || "N/A"}</ValueText>
                  </CompactInfoRow>

                  <CompactInfoRow>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <SchoolIcon sx={{ fontSize: 16, color: "#666" }} />
                      <LabelText>Qualification</LabelText>
                    </Box>
                    <ValueText>{userData.qualification || "N/A"}</ValueText>
                  </CompactInfoRow>

                  {userData.work_experience && (
                    <CompactInfoRow>
                      <LabelText>Work Experience</LabelText>
                      <ValueText>{userData.work_experience}</ValueText>
                    </CompactInfoRow>
                  )}
                </Box>
              </Box>
            </InfoCard>

            {/* Contact Details */}
            <InfoCard>
              <Box className="p-2">
                <SectionTitle>
                  <EmailIcon fontSize="small" />
                  Contact Details
                </SectionTitle>

                <Box sx={{ "& > div": { mb: 1.5 } }}>
                  <CompactInfoRow>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <EmailIcon sx={{ fontSize: 16, color: "#666" }} />
                      <LabelText>Email Address</LabelText>
                    </Box>
                    <ValueText>{userData.email || "N/A"}</ValueText>
                  </CompactInfoRow>

                  <CompactInfoRow>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <PhoneIcon sx={{ fontSize: 16, color: "#666" }} />
                      <LabelText>Phone Number</LabelText>
                    </Box>
                    <ValueText>{userData.phone_number || "N/A"}</ValueText>
                  </CompactInfoRow>

                  <CompactInfoRow>
                    <LabelText>Emergency Contact 1</LabelText>
                    <ValueText>{userData.emergency_contact_one || "N/A"}</ValueText>
                  </CompactInfoRow>

                  <CompactInfoRow>
                    <LabelText>Emergency Contact 2</LabelText>
                    <ValueText>{userData.emergency_contact_two || "N/A"}</ValueText>
                  </CompactInfoRow>

                  <CompactInfoRow>
                    <LabelText>Current Address</LabelText>
                    <ValueText sx={{ textAlign: "right", maxWidth: "150px" }}>
                      {userData.current_address || "N/A"}
                    </ValueText>
                  </CompactInfoRow>

                  <CompactInfoRow>
                    <LabelText>Permanent Address</LabelText>
                    <ValueText sx={{ textAlign: "right", maxWidth: "150px" }}>
                      {userData.permanent_address || "N/A"}
                    </ValueText>
                  </CompactInfoRow>
                </Box>
              </Box>
            </InfoCard>
          </div>

          {/* Middle Column */}
          <div className="col-lg-4">
            {/* Salary Structure */}
            {branchName?.toLowerCase() !== "noida" && (
              <InfoCard>
                <Box className="p-2">
                  <SectionTitle>
                    <AttachMoneyIcon fontSize="small" />
                    Salary Structure
                  </SectionTitle>
                  {[
                    { label: "Basic Salary", value: userData.basic_salary_monthly },
                    { label: "House Rent Allowance", value: userData.hra_monthly },
                    { label: "Dearness Allowance", value: userData.dearness_allowance_monthly },
                    { label: "Conveyance Allowance", value: userData.conveyance_allowance_monthly },
                  ]
                    .filter((i) => i.value > 0)
                    .map((item, i) => (
                      <CompactInfoRow key={i} pb={1}>
                        <LabelText>{item.label}</LabelText>
                        <ValueText>{formatCurrency(item.value)}</ValueText>
                      </CompactInfoRow>
                    ))}

                  <Divider sx={{ my: 0.8 }} />

                  <CompactInfoRow>
                    <SoftTypography sx={{ fontSize: "0.9rem", fontWeight: 600 }} color="success">
                      CTC
                    </SoftTypography>
                    <SoftTypography sx={{ fontSize: "0.9rem", fontWeight: 600 }} color="success">{formatCurrency(salary.earningsMonthly)}</SoftTypography>
                  </CompactInfoRow>

                  {/* Deductions */}
                  <Box sx={{ mb: 0.5 }}>
                    <Typography variant="caption" fontWeight="bold" color="error" fontSize="0.75rem">
                      DEDUCTIONS
                    </Typography>
                  </Box>

                  {[
                    { label: "Provident Fund", value: userData.pf_monthly },
                    { label: "ESI", value: userData.esi_monthly },
                    { label: "Labour Welfare Fund", value: userData.lta_monthly },
                  ]
                    .map((item, i) => (
                      <CompactInfoRow key={i} pb={1}>
                        <LabelText variant="body2" fontSize="0.85rem">
                          {item.label}
                        </LabelText>
                        <Typography variant="body2" color="error" fontSize="0.85rem">
                          - {formatCurrency(item.value)}
                        </Typography>
                      </CompactInfoRow>
                    ))}

                  <Divider sx={{ my: 0.8 }} />

                  {/* Net Salary */}
                  <CompactInfoRow>
                    <SoftTypography sx={{ fontSize: "0.9rem", fontWeight: 600 }} color="info" >In-Hand</SoftTypography>
                    <SoftTypography sx={{ fontSize: "0.9rem", fontWeight: 600 }} color="info">{formatCurrency(salary.inHandMonthly)}</SoftTypography>
                  </CompactInfoRow>
                </Box>
              </InfoCard>
            )}
            {/* Identification */}
            <InfoCard>
              <Box className="p-2">
                <SectionTitle>
                  <BadgeIcon fontSize="small" />
                  Identification
                </SectionTitle>

                <Box sx={{ "& > div": { mb: 1.5 } }}>
                  <CompactInfoRow>
                    <LabelText>Aadhar Number</LabelText>
                    <ValueText>{userData.aadhar_number || "N/A"}</ValueText>
                  </CompactInfoRow>

                  <CompactInfoRow>
                    <LabelText>PAN Number</LabelText>
                    <ValueText>{userData.pan_number || "N/A"}</ValueText>
                  </CompactInfoRow>

                  <CompactInfoRow>
                    <LabelText>UAN Number</LabelText>
                    <ValueText>{userData.uan_no || "N/A"}</ValueText>
                  </CompactInfoRow>

                  <CompactInfoRow>
                    <LabelText>ESI Number</LabelText>
                    <ValueText>{userData.esic_no || "N/A"}</ValueText>
                  </CompactInfoRow>
                </Box>
              </Box>
            </InfoCard>
          </div>

          {/* Right Column */}
          <div className="col-lg-4">
            {/* Bank Details */}
            <InfoCard>
              <Box className="p-2">
                <SectionTitle>
                  <AccountBalanceWalletIcon fontSize="small" />
                  Bank Details
                </SectionTitle>

                <Box sx={{ "& > div": { mb: 1.5 } }}>
                  <CompactInfoRow>
                    <LabelText>Bank Name</LabelText>
                    <ValueText>{userData.bank_name || "N/A"}</ValueText>
                  </CompactInfoRow>

                  {userData.branch_address && (
                    <CompactInfoRow>
                      <LabelText>Branch Address</LabelText>
                      <ValueText sx={{ textAlign: "right", maxWidth: "150px" }}>
                        {userData.branch_address}
                      </ValueText>
                    </CompactInfoRow>
                  )}

                  <CompactInfoRow>
                    <LabelText>Account Number</LabelText>
                    <Typography
                      variant="body1"
                      fontFamily="monospace"
                      fontWeight="500"
                      fontSize="0.85rem"
                    >
                      {userData.account_number
                        ? `•••• ${userData.account_number.slice(-4)}`
                        : "N/A"}
                    </Typography>
                  </CompactInfoRow>

                  <CompactInfoRow>
                    <LabelText>IFSC Code</LabelText>
                    <ValueText>{userData.ifsc_code || "N/A"}</ValueText>
                  </CompactInfoRow>

                  <CompactInfoRow>
                    <LabelText>Esic Number</LabelText>
                    <ValueText>{userData.esi_no || "N/A"}</ValueText>
                  </CompactInfoRow>

                  <CompactInfoRow>
                    <LabelText>UAN Number</LabelText>
                    <ValueText>{userData.uan_no || "N/A"}</ValueText>
                  </CompactInfoRow>
                </Box>
              </Box>
            </InfoCard>

            {/* Documents */}
            <InfoCard>
              <Box className="p-2">
                <SectionTitle>
                  <FolderIcon fontSize="small" />
                  Documents
                </SectionTitle>

                <Box className="row g-1">
                  {[
                    { label: "Resume", icon: <DescriptionIcon />, url: userData.resume },
                    { label: "Profile Photo", icon: <PersonIcon />, url: userData.photo },
                    {
                      label: "Aadhar Card",
                      icon: <FingerprintIcon />,
                      url: userData.upload_aadhar,
                    },
                    { label: "PAN Card", icon: <BadgeIcon />, url: userData.upload_pan },
                    { label: "PF Form", icon: <SecurityIcon />, url: userData.pf_form },
                    { label: "ESI Form", icon: <SecurityIcon />, url: userData.esi_form },
                    { label: "10th Marksheet", icon: <School />, url: userData.upload_tenth },
                    {
                      label: "12th Marksheet",
                      icon: <SchoolOutlined />,
                      url: userData.upload_twelth,
                    },
                    {
                      label: "Graduation",
                      icon: <WorkspacePremium />,
                      url: userData.upload_graduation,
                    },
                    {
                      label: "Post Graduation",
                      icon: <MilitaryTech />,
                      url: userData.upload_post_graduation,
                    },
                    {
                      label: "Pre Exp. Letter",
                      icon: <MilitaryTech />,
                      url: userData.pre_exp_letter,
                    },
                    {
                      label: "Pre Sal. Letter",
                      icon: <MilitaryTech />,
                      url: userData.pre_sal_letter,
                    },
                  ].map((doc, index) => {
                    const enabled = isValidDocUrl(doc.url);
                    const statusColor = enabled ? "#16a34a" : "#dc2626";
                    const statusBg = enabled ? "#ecfdf5" : "#fef2f2";
                    const statusBorder = enabled ? "#22c55e" : "#ef4444";

                    return (
                      <div className="col-6" key={index}>
                        <Box
                          sx={{
                            border: `1.3px solid ${statusBorder}`,
                            background: statusBg,
                            color: statusColor,
                            borderRadius: "7px",
                            minHeight: "48px",
                            px: 0.7,
                            py: 0.45,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            gap: 0.45,
                            cursor: enabled ? "pointer" : "default",
                            transition: "all 0.2s ease",
                            "&:hover": enabled
                              ? {
                                  boxShadow: "0 4px 12px rgba(22, 163, 74, 0.18)",
                                  transform: "translateY(-1px)",
                                }
                              : {},
                          }}
                          onClick={() => enabled && handleViewDocument(doc.url)}
                        >
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            gap={0.5}
                          >
                            <Box display="flex" alignItems="center" gap={0.45} minWidth={0}>
                              {React.cloneElement(doc.icon, {
                                sx: { fontSize: 14, color: statusColor, flexShrink: 0 },
                              })}
                              <Tooltip title={doc.label}>
                                <Typography
                                  sx={{
                                    fontSize: "0.67rem",
                                    fontWeight: 700,
                                    color: statusColor,
                                    textTransform: "uppercase",
                                    lineHeight: 1.1,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {doc.label}
                                </Typography>
                              </Tooltip>
                            </Box>
                            <Typography
                              sx={{
                                fontSize: "0.58rem",
                                fontWeight: 700,
                                color: statusColor,
                                flexShrink: 0,
                              }}
                            >
                              {enabled ? "Uploaded" : "Missing"}
                            </Typography>
                          </Box>
                        </Box>
                      </div>
                    );
                  })}
                </Box>
              </Box>
            </InfoCard>
          </div>
        </Box>

        {/* Education Details - Full Width */}
        <div className="row mt-1">
          <div className="col-12">
            <InfoCard>
              <Box className="p-2">
                <SectionTitle sx={{ mb: 0.8 }}>
                  <SchoolIcon fontSize="small" />
                  Education Details
                </SectionTitle>

                <Box sx={{ overflowX: "auto" }}>
                  <table
                    className="table table-bordered table-sm"
                    style={{ marginBottom: 0, fontSize: "0.85rem" }}
                  >
                    <thead style={{ background: "#f6f8fb" }}>
                      <tr>
                        {["", "Exam", "Board", "Institute", "Year", "%", "Mode"].map((h) => (
                          <th key={h} style={{ padding: "6px 8px" }}>
                            <LabelText fontSize="0.8rem">{h}</LabelText>
                          </th>
                        ))}
                      </tr>
                    </thead>

                    <tbody>
                      {educationRows.map((row, i) => (
                        <tr key={i}>
                          <td style={{ padding: "6px 8px" }}>
                            <LabelText fontWeight="bold" fontSize="0.85rem">
                              {row.level}
                            </LabelText>
                          </td>

                          <td style={{ padding: "6px 8px" }}>
                            <ValueText fontSize="0.85rem">{row.exam || "—"}</ValueText>
                          </td>

                          <td style={{ padding: "6px 8px" }}>
                            <ValueText fontSize="0.85rem">{row.board || "—"}</ValueText>
                          </td>

                          <td style={{ padding: "6px 8px" }}>
                            <ValueText fontSize="0.85rem" sx={{ maxWidth: 140 }} noWrap>
                              {row.institute || "—"}
                            </ValueText>
                          </td>

                          <td style={{ padding: "6px 8px" }}>
                            <ValueText fontSize="0.85rem">{row.year || "—"}</ValueText>
                          </td>

                          <td style={{ padding: "6px 8px" }}>
                            <ValueText fontSize="0.85rem">{row.percentage || "—"}</ValueText>
                          </td>

                          <td style={{ padding: "6px 8px" }}>
                            <ValueText fontSize="0.85rem">{row.mode || "—"}</ValueText>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              </Box>
            </InfoCard>
          </div>
        </div>
      </Box>

      {/* Change Password Dialog */}
      <Dialog open={changePasswordOpen} onClose={handleCloseChangePassword} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ py: 1.5, px: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <KeyIcon fontSize="small" />
            <span style={{ fontSize: "1rem" }}>Change Password</span>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ py: 1, px: 2 }}>
          <DialogContentText sx={{ mb: 2, fontSize: "0.9rem" }}>
            Please enter your current password and new password to update.
          </DialogContentText>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <FormField
              label="Current Password"
              placeholder="Enter current password"
              value={passwordForm.current_password}
              onChange={handlePasswordChange("current_password")}
              inputProps={{ type: "password", autoComplete: "off" }}
              error={!!passwordErrors.current_password}
              helperText={passwordErrors.current_password}
              fullWidth
              size="small"
            />

            <FormField
              label="New Password"
              placeholder="Enter new password"
              value={passwordForm.new_password}
              onChange={handlePasswordChange("new_password")}
              inputProps={{ type: "password", autoComplete: "off" }}
              error={!!passwordErrors.new_password}
              helperText={passwordErrors.new_password}
              fullWidth
              size="small"
            />

            <FormField
              label="Confirm New Password"
              placeholder="Confirm new password"
              value={passwordForm.new_password_confirmation}
              onChange={handlePasswordChange("new_password_confirmation")}
              inputProps={{ type: "password", autoComplete: "off" }}
              error={!!passwordErrors.new_password_confirmation}
              helperText={passwordErrors.new_password_confirmation}
              fullWidth
              size="small"
            />

            {/* Password Requirements */}
            <Box sx={{ mt: 1.5, p: 1.5, bgcolor: "#f9f9f9", borderRadius: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom fontSize="0.85rem">
                Password Requirements:
              </Typography>
              <ul style={{ margin: 0, paddingLeft: "18px" }}>
                <li>
                  <Typography variant="body2" color="textSecondary" fontSize="0.8rem">
                    Minimum 6 characters
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" color="textSecondary" fontSize="0.8rem">
                    One special character
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" color="textSecondary" fontSize="0.8rem">
                    One number (2 are recommended)
                  </Typography>
                </li>
              </ul>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ py: 1, px: 2 }}>
          <SoftButton
            onClick={handleCloseChangePassword}
            color="secondary"
            variant="outlined"
            size="small"
          >
            Cancel
          </SoftButton>
          <SoftButton
            onClick={handleChangePasswordSubmit}
            color="info"
            variant="gradient"
            disabled={changePasswordLoading}
            size="small"
          >
            {changePasswordLoading ? "Updating..." : "Update Password"}
          </SoftButton>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <SoftSnackbar
        color={snackbar.color}
        icon="notifications"
        title="Notification"
        content={snackbar.message}
        open={snackbar.open}
        close={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        time={3000}
      />

      <Footer />
    </DashboardLayout>
  );
}

export default UserProfile;
